// TEMP-WRAPPER: this whole file exists only for the WebView wrapper app and
// is deleted when the app goes native.

type TinyAppHandshake = {
  commands?: string[];
  platform?: string;
  version?: string | null;
  build?: string | null;
};

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(message: string): void };
    __tinyApp?: TinyAppHandshake;
  }
}

// Injected only inside the app's WebView; __tinyApp lists the commands the
// installed build understands (app builds lag portal deploys).
export const isInMobileApp = () => 'ReactNativeWebView' in window;

export const canUseMobileAppCommand = (kind: string) =>
  isInMobileApp() && !!window.__tinyApp?.commands?.includes(kind);

export const sendMobileAppCommand = (
  kind: string,
  payload: Record<string, unknown>,
  capability = kind,
): boolean => {
  if (!window.ReactNativeWebView || !canUseMobileAppCommand(capability)) {
    return false;
  }
  window.ReactNativeWebView.postMessage(JSON.stringify({ kind, payload }));
  return true;
};

export const ACTIVATE_KIT_COMMAND = 'activate-kit';
export const ACTIVATE_KIT_EVENT = 'tinyapp:activate-kit';

export type MobileAppScanStatus = 'scanned' | 'denied' | 'unavailable';

export type MobileAppKitActivationEvent =
  | { type: 'submitted'; kitId: string; method: 'scan' | 'manual' }
  | { type: 'cancelled' }
  | { type: 'scan-opened' }
  | { type: 'scan-result'; status: MobileAppScanStatus };

export const canActivateKitWithMobileApp = () =>
  canUseMobileAppCommand(ACTIVATE_KIT_COMMAND);

export const showMobileAppKitActivation = () =>
  sendMobileAppCommand(ACTIVATE_KIT_COMMAND, {});

export const hideMobileAppKitActivation = () =>
  sendMobileAppCommand(`${ACTIVATE_KIT_COMMAND}-hide`, {}, ACTIVATE_KIT_COMMAND);

export const failMobileAppKitActivation = (message: string) =>
  sendMobileAppCommand(
    `${ACTIVATE_KIT_COMMAND}-error`,
    { message },
    ACTIVATE_KIT_COMMAND,
  );

export const onMobileAppKitActivation = (
  handler: (event: MobileAppKitActivationEvent) => void,
) => {
  const listener = (event: Event) =>
    handler((event as CustomEvent<MobileAppKitActivationEvent>).detail);
  window.addEventListener(ACTIVATE_KIT_EVENT, listener);
  return () => window.removeEventListener(ACTIVATE_KIT_EVENT, listener);
};

export type MobileAppPlatform = 'ios' | 'android' | 'mobile_app';

export type MobileAppInfo = {
  platform: MobileAppPlatform;
  version?: string;
  build?: string;
};

const NATIVE_PLATFORMS: MobileAppPlatform[] = ['ios', 'android'];

export const getMobileAppInfo = (): MobileAppInfo | null => {
  if (!isInMobileApp()) return null;

  const app = window.__tinyApp;
  const platform = NATIVE_PLATFORMS.find(known => known === app?.platform);

  return {
    platform: platform ?? 'mobile_app',
    version: app?.version ?? undefined,
    build: app?.build ?? undefined,
  };
};
